import { Component, NO_ERRORS_SCHEMA, OnInit, inject } from '@angular/core';
import { UpdatePageTitle } from '../../../../store/app/app.actions';
import { Store } from '@ngxs/store';
import { FaqApiService } from '../../../../_services/faqs-service/faq-api.service';
import { FaqCategory, Faq } from '../../../../_interfaces/faq.interface';
import { MarkdownComponent } from 'ngx-markdown';
import {
  NgbCollapse,
  NgbDropdown,
  NgbDropdownButtonItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbDropdownModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { HeaderBannerComponent } from '../../../../shared/components/header-banner/header-banner.component';

@Component({
  selector: 'app-faqs',
  imports: [
    MarkdownComponent,
    NgbCollapse,
    NgbDropdownModule,
    NgbDropdownMenu,
    NgbDropdownButtonItem,
    NgbDropdown,
    NgbDropdownToggle,
    HeaderBannerComponent,
  ],
  schemas: [NO_ERRORS_SCHEMA], // needed for ngbCollapse (and other nonstandard) properties on HTML elements
  templateUrl: './faqs.component.html',
})
export class FaqsComponent implements OnInit {
  readonly store = inject(Store);
  readonly faqApiService = inject(FaqApiService);

  protected faqDetails: Faq[] = [];
  protected faqCategories: FaqCategory[] = [];
  private categoryFaqs: { [key: string]: Faq[] } = {}; // Mapping of category slugs to FAQs
  protected expandedIndex: number | null = null;
  protected activeCategory: string | null = null;
  readonly tagsToShow: string = 'admin_help_center';

  ngOnInit() {
    this.store.dispatch(new UpdatePageTitle('Help Center'));
    this.loadCategoriesAndFaqs(); // Load categories and filter based on FAQs
  }

  // Fetch and filter categories based on available FAQs
  loadCategoriesAndFaqs() {
    this.faqApiService
      .getFaqCategories()
      .pipe(
        switchMap((categories) => {
          // Create an array of observables to fetch FAQs for each category
          const categoryRequests = categories.map((category) =>
            this.faqApiService.getFaqDetails(this.tagsToShow, category.slug).pipe(
              tap((faqs) => {
                // Store FAQs in the mapping if there are any
                if (faqs.length > 0) this.categoryFaqs[category.slug] = faqs;
              }),
              catchError(() => of([])), // Handle errors and return an empty array
            ),
          );

          // Process each category request
          return this.fetchCategoriesWithFAQs(categories, categoryRequests);
        }),
        tap((filteredCategories) => {
          // Set the active category if available
          if (filteredCategories.length > 0) {
            this.faqCategories = filteredCategories;
            this.setActiveCategory(filteredCategories[0].slug); // Set the first category as active
          }
        }),
      )
      .subscribe({
        error: (error) => console.error('Error loading categories and FAQs:', error),
      });
  }

  // Function to combine category fetch requests
  fetchCategoriesWithFAQs(categories: FaqCategory[], categoryRequests: Observable<Faq[]>[]): Observable<FaqCategory[]> {
    return new Observable((observer) => {
      let completed = 0;
      // Use an object to store results by index to preserve order
      const resultsByIndex: { [index: number]: FaqCategory | null } = {};

      categoryRequests.forEach((request, index) => {
        request.subscribe((faqs) => {
          const category = categories[index];
          // Store category at its original index, or null if no FAQs
          resultsByIndex[index] = faqs.length > 0 ? category : null;
          completed++;

          if (completed === categoryRequests.length) {
            // Build final results array in original order, filtering out nulls
            const results: FaqCategory[] = [];
            for (let i = 0; i < categories.length; i++) {
              if (resultsByIndex[i] !== null) {
                results.push(resultsByIndex[i]!);
              }
            }
            observer.next(results);
            observer.complete();
          }
        });
      });
    });
  }

  // Fetch FAQs for the selected category
  getFaqs(categorySlug: string) {
    this.faqDetails = this.categoryFaqs[categorySlug] || [];
  }

  // Set the active category and fetch its FAQs
  setActiveCategory(slug: string) {
    if (this.activeCategory !== slug) {
      this.activeCategory = slug;
      this.getFaqs(slug); // Fetch FAQs for the selected category
    }
  }

  // Get the name of the active category
  getActiveCategoryName(): string | undefined {
    const activeCategory = this.faqCategories.find((category) => category.slug === this.activeCategory);
    return activeCategory ? activeCategory.name : undefined;
  }

  // Toggle FAQ collapse
  toggleCollapse(index: number) {
    this.expandedIndex = this.expandedIndex === index ? null : index;
  }
}
